import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Maximize2, X, Sparkles, Monitor, PenTool } from "lucide-react";
import { DIAGRAMS } from "./diagrams";
import { cn } from "../../lib/utils";

const KIND = {
  diagram: { label: "Diagram", icon: PenTool },
  screenshot: { label: "Annotated screenshot · sample data, personal details masked", icon: Monitor },
  ai: { label: "AI-generated illustration", icon: Sparkles },
};

function Marker({ n, x, y, active, onHover }) {
  return (
    <span onMouseEnter={() => onHover(n)} onMouseLeave={() => onHover(null)}
          className={cn("absolute -translate-x-1/2 -translate-y-1/2 w-6 h-6 rounded-full flex items-center justify-center font-mono-tabular text-[11px] font-bold shadow-lg ring-2 transition-transform",
            "bg-warning text-black ring-black/40", active && "scale-125 ring-white")}
          style={{ left: `${x}%`, top: `${Math.min(97, y)}%` }}>{n}</span>
  );
}

function Lightbox({ v, onClose }) {
  useEffect(() => {
    const k = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);
  // Portal to <body>: the figure's kc-rise animation makes it the containing block for position:fixed.
  return createPortal(
    <div data-testid="kc-lightbox" className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in" onClick={onClose}>
      <button data-testid="kc-lightbox-close" aria-label="Close" className="absolute top-4 right-4 w-9 h-9 rounded-sm bg-white/10 text-white flex items-center justify-center hover:bg-white/20"><X size={16} /></button>
      <img src={v.src} alt={v.caption} className="max-w-full max-h-full rounded-sm shadow-2xl" onClick={e => e.stopPropagation()} />
    </div>,
    document.body
  );
}

function Shot({ v, testid }) {
  const [hover, setHover] = useState(null);
  const [zoom, setZoom] = useState(false);
  return (
    <>
      <div className="relative border border-border rounded-sm overflow-hidden bg-black group">
        <img src={v.src} alt={v.caption} loading="lazy" className="w-full h-auto block" />
        {v.notes.map((n, i) => <Marker key={i} n={i + 1} x={n.x} y={n.y} active={hover === i + 1} onHover={setHover} />)}
        <button onClick={() => setZoom(true)} data-testid={`${testid}-zoom`} aria-label="Enlarge screenshot"
                className="absolute bottom-2 right-2 w-8 h-8 rounded-sm bg-black/60 text-white flex items-center justify-center opacity-80 hover:opacity-100 transition-opacity"><Maximize2 size={13} /></button>
      </div>
      <ol className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
        {v.notes.map((n, i) => (
          <li key={i} data-testid={`${testid}-note-${i + 1}`} onMouseEnter={() => setHover(i + 1)} onMouseLeave={() => setHover(null)}
              className={cn("flex items-start gap-2 text-[12.5px] leading-snug rounded-sm px-1 -mx-1 transition-colors", hover === i + 1 && "bg-warning/10")}>
            <span className="w-5 h-5 shrink-0 rounded-full bg-warning text-black font-mono-tabular text-[10.5px] font-bold flex items-center justify-center">{i + 1}</span>
            <span className="pt-0.5">{n.text}</span>
          </li>
        ))}
      </ol>
      {zoom && <Lightbox v={v} onClose={() => setZoom(false)} />}
    </>
  );
}

// One illustration: drawn diagram, annotated screenshot or AI picture, each labelled by kind.
export default function Visual({ v, testid }) {
  const kind = KIND[v.kind];
  const Diagram = v.kind === "diagram" ? DIAGRAMS[v.diagram] : null;
  return (
    <figure data-testid={testid} data-kind={v.kind} className="my-6 kc-rise">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] font-semibold text-muted mb-2">
        <kind.icon size={11} className="text-primary" /> {kind.label}
      </div>
      {v.kind === "screenshot" && <Shot v={v} testid={testid} />}
      {v.kind === "ai" && <img src={v.src} alt={v.caption} loading="lazy" className="w-full h-auto rounded-sm border border-border" />}
      {Diagram && <div className="tile p-4 sm:p-5 overflow-x-auto"><Diagram {...v.data} /></div>}
      <figcaption className="mt-2 text-[12px] text-muted leading-snug">{v.caption}</figcaption>
    </figure>
  );
}
